import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  IconButton,
  Alert,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { 
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { Question, QuestionPreference } from '../../types';
import { questionPreferences, flows } from '../../services/api';
import { useNavigate } from 'react-router-dom';

interface QuestionListProps {
  questions: Question[];
  droppableId: string;
  title: string;
}

const QuestionList: React.FC<QuestionListProps> = ({ questions, droppableId, title }) => (
  <Paper sx={{ p: 2, height: '100%', minHeight: '500px' }}>
    <Typography variant="h6" gutterBottom>
      {title}
    </Typography>
    <Droppable droppableId={droppableId}>
      {(provided) => (
        <Box
          ref={provided.innerRef}
          {...provided.droppableProps}
          sx={{ minHeight: '100%' }}
        >
          {questions.map((question, index) => (
            <Draggable 
              key={question.id} 
              draggableId={String(question.id)} 
              index={index}
            >
              {(provided) => (
                <Paper
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  sx={{ 
                    p: 2, 
                    mb: 1, 
                    bgcolor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    }
                  }}
                >
                  <IconButton 
                    size="small" 
                    {...provided.dragHandleProps}
                    sx={{ mr: 1 }}
                  >
                    <DragIcon />
                  </IconButton>
                  <Typography>{question.text}</Typography>
                </Paper>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </Box>
      )}
    </Droppable>
  </Paper>
);

const DailyCheckinCustomization: React.FC = () => {
  const navigate = useNavigate();
  const [availableQuestions, setAvailableQuestions] = useState<Question[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preference, setPreference] = useState<QuestionPreference | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      // Get all daily checkin questions
      const flowResponse = await flows.getQuestions('daily_checkin_v0');
      const allQuestions = flowResponse.data;

      // Get user's existing preferences
      const preferencesResponse = await questionPreferences.getAll('daily_checkin_v0');
      console.log('User preferences:', preferencesResponse.data);
      
      if (preferencesResponse.data && preferencesResponse.data.length > 0) {
        // Find the daily checkin preference
        const dailyCheckinPreference = preferencesResponse.data[0];
        console.log('Daily checkin preference:', dailyCheckinPreference);
        
        setPreference(dailyCheckinPreference);
        
        // Extract question objects from the preference
        if (dailyCheckinPreference.questions && dailyCheckinPreference.questions.length > 0) {
          // If questions are full objects
          if (typeof dailyCheckinPreference.questions[0] === 'object') {
            const preferredQuestions = dailyCheckinPreference.questions as unknown as Question[];
            setSelectedQuestions(preferredQuestions);
            
            // Filter out selected questions from available questions
            const selectedIds = new Set(preferredQuestions.map(q => q.id));
            setAvailableQuestions(allQuestions.filter((q: Question) => !selectedIds.has(q.id)));
          } 
          // If questions are just IDs
          else {
            const selectedIds = new Set(dailyCheckinPreference.questions as number[]);
            setSelectedQuestions(allQuestions.filter((q: Question) => selectedIds.has(q.id as number)));
            setAvailableQuestions(allQuestions.filter((q: Question) => !selectedIds.has(q.id as number)));
          }
        } else {
          setAvailableQuestions(allQuestions);
        }
      } else {
        setAvailableQuestions(allQuestions);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading questions:', error);
      setError('Failed to load questions. Please try again.');
      setLoading(false);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceList = result.source.droppableId === 'available' 
      ? availableQuestions 
      : selectedQuestions;
    const destList = result.destination.droppableId === 'available' 
      ? availableQuestions 
      : selectedQuestions;

    const [movedQuestion] = sourceList.splice(result.source.index, 1);
    destList.splice(result.destination.index, 0, movedQuestion);

    setAvailableQuestions([...availableQuestions]);
    setSelectedQuestions([...selectedQuestions]);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);
      const data = {
        flow: 'daily_checkin_v0',
        questions: selectedQuestions.map((question: Question) => question.id as number),
        is_active: true
      };

      // Always create a new preference
      await questionPreferences.create(data);

      // Reset the flow and navigate to home
      await flows.resetFlow('daily_checkin_v0');
      navigate('/home');

      setSaving(false);
    } catch (error) {
      console.error('Error saving preferences:', error);
      setError('Failed to save preferences. Please try again.');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth={false}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Customize Daily Check-in Questions
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Drag and drop questions between the columns to customize your daily check-in.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <QuestionList
              questions={availableQuestions}
              droppableId="available"
              title="Available Questions"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <QuestionList
              questions={selectedQuestions}
              droppableId="selected"
              title="Selected Questions"
            />
          </Grid>
        </Grid>
      </DragDropContext>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          size="large"
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </Box>
    </Container>
  );
};

export default DailyCheckinCustomization;
